// Internal analytics client — no third-party dependencies.
// Tracking ONLY runs after explicit user consent.

const VISITOR_COOKIE = "plateia_vid";
const SESSION_KEY = "plateia_sid";
const SESSION_LAST_KEY = "plateia_sid_last";
const CONSENT_KEY = "plateia_analytics_consent"; // 'accepted' | 'declined'
const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 min
const FLUSH_INTERVAL_MS = 5000;
const MAX_BATCH = 25;

const PROJECT_ID = (import.meta as any).env?.VITE_SUPABASE_PROJECT_ID || "";
const ENDPOINT = `https://${PROJECT_ID}.supabase.co/functions/v1/track-event`;

type EventPayload = {
  visitor_id: string;
  session_id: string;
  event_name: string;
  page?: string;
  metadata?: Record<string, unknown>;
  user_id?: string | null;
  referrer?: string | null;
  device?: string | null;
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
  user_agent?: string | null;
  browser?: string | null;
  os?: string | null;
};

let queue: EventPayload[] = [];
let flushTimer: number | null = null;
let lastPageView: { page: string; ts: number } | null = null;
let sessionUtm: { source?: string; medium?: string; campaign?: string } | null = null;
let sessionPrimed = false;

// ---------- Consent ----------
export function getConsent(): "accepted" | "declined" | null {
  if (typeof window === "undefined") return null;
  const v = localStorage.getItem(CONSENT_KEY);
  return v === "accepted" || v === "declined" ? v : null;
}

export function setConsent(value: "accepted" | "declined") {
  if (typeof window === "undefined") return;
  localStorage.setItem(CONSENT_KEY, value);
  if (value === "declined") {
    // remove visitor cookie
    document.cookie = `${VISITOR_COOKIE}=; Path=/; Max-Age=0; SameSite=Lax`;
    sessionStorage.removeItem(SESSION_KEY);
    sessionStorage.removeItem(SESSION_LAST_KEY);
    queue = [];
  }
  window.dispatchEvent(new CustomEvent("plateia-consent-changed", { detail: value }));
}

// ---------- IDs ----------
function uuid(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function getCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  return match ? decodeURIComponent(match[1]) : null;
}

function setCookie(name: string, value: string, days: number) {
  if (typeof document === "undefined") return;
  const exp = new Date(Date.now() + days * 86400 * 1000).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Expires=${exp}; SameSite=Lax`;
}

function getVisitorId(): string {
  let id = getCookie(VISITOR_COOKIE);
  if (!id) {
    id = uuid();
    setCookie(VISITOR_COOKIE, id, 365);
  }
  return id;
}

function getSessionId(): string {
  const now = Date.now();
  const last = Number(sessionStorage.getItem(SESSION_LAST_KEY) || 0);
  let sid = sessionStorage.getItem(SESSION_KEY);
  if (!sid || (last && now - last > SESSION_TIMEOUT_MS)) {
    sid = uuid();
    sessionStorage.setItem(SESSION_KEY, sid);
    sessionPrimed = false;
    sessionUtm = null;
  }
  sessionStorage.setItem(SESSION_LAST_KEY, String(now));
  return sid;
}

// ---------- Device ----------
function detectDevice(): string {
  const ua = navigator.userAgent;
  if (/iPad|Tablet|PlayBook/i.test(ua)) return "tablet";
  if (/Mobi|Android|iPhone|iPod/i.test(ua)) return "mobile";
  return "desktop";
}

function detectBrowser(): string {
  const ua = navigator.userAgent;
  if (/Edg\//.test(ua)) return "Edge";
  if (/Chrome\//.test(ua) && !/Edg|OPR/.test(ua)) return "Chrome";
  if (/Firefox\//.test(ua)) return "Firefox";
  if (/Safari\//.test(ua) && !/Chrome|Edg/.test(ua)) return "Safari";
  if (/OPR\//.test(ua)) return "Opera";
  return "Other";
}

function detectOS(): string {
  const ua = navigator.userAgent;
  if (/Windows/.test(ua)) return "Windows";
  if (/Android/.test(ua)) return "Android";
  if (/iPhone|iPad|iPod/.test(ua)) return "iOS";
  if (/Mac OS X/.test(ua)) return "macOS";
  if (/Linux/.test(ua)) return "Linux";
  return "Other";
}

// ---------- UTM ----------
function captureUtmFromUrl() {
  if (sessionUtm) return;
  try {
    const params = new URLSearchParams(window.location.search);
    const source = params.get("utm_source") || undefined;
    const medium = params.get("utm_medium") || undefined;
    const campaign = params.get("utm_campaign") || undefined;
    if (source || medium || campaign) sessionUtm = { source, medium, campaign };
    else sessionUtm = {};
  } catch {
    sessionUtm = {};
  }
}

// ---------- Queue & flush ----------
function scheduleFlush() {
  if (flushTimer != null) return;
  flushTimer = window.setTimeout(flush, FLUSH_INTERVAL_MS);
}

async function flush(useBeacon = false) {
  if (flushTimer != null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (queue.length === 0) return;
  const batch = queue.splice(0, MAX_BATCH);
  const payload = JSON.stringify({ events: batch });

  try {
    if (useBeacon && "sendBeacon" in navigator) {
      const blob = new Blob([payload], { type: "application/json" });
      navigator.sendBeacon(ENDPOINT, blob);
    } else {
      await fetch(ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: payload,
        keepalive: true,
      });
    }
  } catch (err) {
    // Best-effort; drop on failure to avoid loops
    console.warn("[analytics] flush failed", err);
  }
}

// ---------- Public API ----------
export function trackEvent(
  eventName: string,
  metadata: Record<string, unknown> = {},
  opts: { page?: string; userId?: string | null } = {}
) {
  if (typeof window === "undefined") return;
  if (getConsent() !== "accepted") return;

  captureUtmFromUrl();

  const visitor_id = getVisitorId();
  const session_id = getSessionId();
  const page = opts.page || window.location.pathname + window.location.search;
  const device = detectDevice();

  const payload: EventPayload = {
    visitor_id,
    session_id,
    event_name: eventName,
    page,
    metadata,
    user_id: opts.userId ?? null,
    referrer: document.referrer || null,
    device,
  };

  // Attach session-priming metadata on first event of the session
  if (!sessionPrimed) {
    payload.utm_source = sessionUtm?.source ?? null;
    payload.utm_medium = sessionUtm?.medium ?? null;
    payload.utm_campaign = sessionUtm?.campaign ?? null;
    payload.user_agent = navigator.userAgent;
    payload.browser = detectBrowser();
    payload.os = detectOS();
    sessionPrimed = true;
  }

  queue.push(payload);
  if (queue.length >= MAX_BATCH) flush();
  else scheduleFlush();
}

export function trackPageView(page?: string, userId?: string | null) {
  if (getConsent() !== "accepted") return;
  const p = page || window.location.pathname + window.location.search;
  const now = Date.now();
  if (lastPageView && lastPageView.page === p && now - lastPageView.ts < 1000) return;
  lastPageView = { page: p, ts: now };
  trackEvent("page_view", {}, { page: p, userId });
}

// Auto-flush before page unload
if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", () => flush(true));
  window.addEventListener("pagehide", () => flush(true));
  // Click delegation: any element with data-track="name"
  document.addEventListener(
    "click",
    (e) => {
      if (getConsent() !== "accepted") return;
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const el = target.closest<HTMLElement>("[data-track]");
      if (!el) return;
      const name = el.getAttribute("data-track");
      if (!name) return;
      const meta: Record<string, unknown> = { label: el.textContent?.trim().slice(0, 80) || null };
      // copy data-track-* extras
      for (const attr of Array.from(el.attributes)) {
        if (attr.name.startsWith("data-track-") && attr.name !== "data-track") {
          meta[attr.name.replace("data-track-", "")] = attr.value;
        }
      }
      trackEvent("click", { ...meta, action: name });
    },
    { capture: true }
  );
}
